import { ApolloProvider } from '@apollo/client';
import { ConfigProvider } from 'antd';
import deDE from 'antd/lib/locale/de_DE';
import moment from 'moment';
import 'moment/locale/de';
import type { AppProps } from 'next/app';
import { useRouter } from 'next/router';
import React, { useMemo } from 'react';
import { IntlProvider } from 'react-intl';
import { useMediaQuery } from 'react-responsive';
import { client } from '../apollo/ApolloSetup';
import '../helpers/arrayext';
import { hardenRouter } from '../lib/harden-router';
import German from '../locales/de-DE';
import { default as English, default as enUS } from '../locales/en-US';
import '../styles/globals.css';

interface ScreenSizeI {
  isMobile: boolean;
}

export const ScreenSizeContext = React.createContext<ScreenSizeI>({
  isMobile: false,
});

/**
 * This provides strict router policies such that re-directs are only done if they point
 * to valid route destinations defined in the /pages directory.
 *
 * The generation of the pages tree has to happen before the build. For local testing, run the script
 *
 *
 */
hardenRouter({ fallback: '/home' });

function AveliosMedicalPatientPortal({ Component, pageProps }: AppProps) {
  const { locale } = useRouter();
  const [shortLocale] = locale ? locale.split('-') : ['de'];

  const messages = useMemo(() => {
    switch (shortLocale) {
      case 'de':
        return German;
      case 'en':
        return English;
      default:
        return German;
    }
  }, [shortLocale]);

  const antdLocale = useMemo(() => {
    switch (shortLocale) {
      case 'de':
        return deDE;
      case 'en':
        return enUS;
      default:
        return deDE;
    }
  }, [shortLocale]);

  const isMobile = !useMediaQuery({ query: '(min-width:1020px)' });

  moment.locale(shortLocale);

  return (
    <>
      <div style={{ scrollBehavior: 'smooth' }}>
        {/* @ts-ignore */}
        <ConfigProvider locale={antdLocale}>
          {/* Removed as part of CLN-18325 */}
          {/*<GoogleOAuthProvider clientId={process.env.NEXT_PUBLIC_GOOGLE_O_AUTH_CLIENT_ID ?? ''}>*/}
          <ScreenSizeContext.Provider value={{ isMobile }}>
            <IntlProvider locale={shortLocale} messages={messages} onError={() => null}>
              <ApolloProvider client={client}>
                <Component {...pageProps} />
              </ApolloProvider>
            </IntlProvider>
          </ScreenSizeContext.Provider>
          {/*</GoogleOAuthProvider>*/}
        </ConfigProvider>
      </div>
    </>
  );
}

export default AveliosMedicalPatientPortal;
